// 🧊 «모션 브리핑» 무대 — 밝은 캔버스 위 도형 하나가 장면마다 변신하고, 그 안에 장표가 들어온다(원쉐이프 화법).
//   Overlay169 장표 층의 래퍼(스킨이 `blueprintMotion` 일 때만 낀다 · 09-30 부모 승인). 정본 지도 §12-5.
//   ★장표 10종은 «안 내용»으로 그대로 쓴다★ — 도형 목표 모양 안쪽을 `SlideStageInsetContext` 로 내려보내면
//   `useSafeArea` 가 그 안에 배치한다. 채팅 편집·장표 편집 계약·직접 수정·EF 는 이 파일을 모른다.
//   순수 렌더: 같은 (프레임·장면·앞 장면) = 같은 그림. 난수·시계·프레임 간 상태 없음.
import React from 'react';
import { AbsoluteFill, useVideoConfig } from 'remotion';
import { DURATION_MS, EASE_ENTER_SOFT, lin, win } from '../../lib/motion-kit/spring';
import { safeInsetPx } from '../../lib/safe-zone';
import { inferAspect } from './layouts';
import {
  BRIEF_CONTENT_FADE_SEC, BRIEF_CONTENT_TAIL_SEC, BRIEF_PAD, BRIEF_PAPER,
  briefContentLead, briefFillColor, briefFullTextSubCols, briefGlueLastWords, briefFillOf, briefFit, briefLayoutInputs, briefMixHex, briefMorphRect, briefSeedRect, briefTargetRect, type BriefSlide,
} from './motion-brief-shape';
import { SlideStageInsetContext } from './stage-inset';
import { useBalancedTitle } from './title-balance';
import { KeywordText, KitWords, type KeywordEmphasisId } from './motion';
import { FW, KO_TEXT, LINE_HEIGHT, type AtmosphereColors } from './tokens';
import { titleCueLeadSec } from "../../lib/templates/word-cues";

export interface MotionBriefStageProps {
  /** 씬 상대 프레임(장면 시작 = 0). */
  f: number;
  slide: BriefSlide;
  /** 바로 앞 장면의 장표 — 없으면(첫 장면·앞이 영상 씬) 가운데 씨앗에서 자란다. */
  prevSlide: BriefSlide | null;
  /** 장면 길이(프레임) — 안 내용이 다음 변신 0.1초 전에 빠지는 기준. 모르면 빠지지 않는다. */
  sceneFrames?: number;
  canvas: string;
  /** 브랜드색(도형 색 «brand» 에 칠한다 — 원래 색 그대로 · 글자색은 밝기로 고른다). */
  brand: string;
  /** 오른쪽에 비워 둘 폭(px) — 프레젠터 예약 컬럼. */
  reserveRightPx?: number;
  /** 하단 자막 띠 윗변·아랫변 y(px · 장면 최대 줄 수 기준) — 도형은 윗변 24px 위에서 멈추거나, 글이 닿으면 띠를 품는다. 자막이 꺼졌으면 생략. */
  captionTopPx?: number;
  captionBottomPx?: number;
  /** 이 장면에서 가장 넓은 자막 띠 폭(px). */
  captionWidthPx?: number;
  /** 앞 장면의 자막 띠(그 장면 최대 줄 수 기준) — 앞 도형은 «그 장면에서 보였던 모양»에서 출발해야 경계에서 튀지 않는다. */
  prevCaption?: BriefCaption;
  /** 도형 위 큰 제목의 글자색·강조색(캔버스 위 · 종이 팔레트). 없으면 큰 제목 분리를 하지 않는다. */
  titleColors?: AtmosphereColors;
  /** 영상의 첫 장면 — 앞에 보여 줄 그림이 없으니 캔버스는 0프레임부터 켜져 있다(도형만 씨앗에서 자란다). */
  opening?: boolean;
  /** ⏱🔤 낱말 신호(씬 상대 프레임 · 2026-10-04 · `word-cues.slideWordCuesFrom`) — 큰 제목은 그 낱말을 말할 때 한 자씩.
   *  없으면 종전(무대가 들어온 직후). 항목 시각은 안 장표(`itemCues`)가 쓴다 — 무대는 «다 켜진 뒤»를 잴 때만 본다. */
  wordCues?: { items?: ReadonlyArray<number | null>; title?: number | null };
  children: React.ReactNode;
}

/** 자막 띠 윗변·아랫변(px)과 그 장면 가장 넓은 띠 폭(px · 도형이 띠를 품을 수 있는지 가른다). */
export interface BriefCaption { top: number; bottom: number; width?: number }

/** 무대 기하 — 글자 자리 인셋(자막 띠 위까지)과 자막 띠를 품을 때의 도형 아랫변. 들어오는 무대·나가는 무대가 같은 식을 쓴다. */
function stageGeom(W: number, H: number, reserveRightPx: number, caption?: BriefCaption) {
  const k = W / 1920;
  const core = safeInsetPx(inferAspect(W, H), W, H, 'core');
  // 자막 띠는 세이프존 core 안쪽(아래 끝)에 앉는다(실측 806~895px) — 글자 자리는 그 윗변 24px 위까지만.
  const bottom = caption ? Math.max(core.bottom, H - caption.top + Math.round(24 * k)) : core.bottom;
  const avail = { ...core, right: core.right + reserveRightPx, bottom };
  // 도형이 자막 띠를 품을 때의 아랫변 — 띠 아랫변 + 여백.
  const tail = caption ? caption.bottom + Math.round(BRIEF_PAD.y * k) : undefined;
  return { k, core, avail, tail, tailWidth: caption?.width };
}

/** 촘촘히 다시 맞출 수 있는 장표 — 글자를 줄이기 전에 빈틈부터 줄인다. */
const BRIEF_COMPACTABLE: ReadonlySet<string> = new Set(['checkList', 'stepFlow', 'cardGrid']);

/** 이 장면의 도형·안 내용 계획(순수 · 무대와 경계 불변식 테스트가 같은 식을 쓴다). */
export function briefStageFit(slide: BriefSlide, W: number, H: number, reserveRightPx: number, caption: BriefCaption | undefined, split: boolean) {
  const { k, core, avail: baseAvail, tail, tailWidth } = stageGeom(W, H, reserveRightPx, caption);
  // 큰 제목 분리 — 목록형 5종은 제목을 도형 위 캔버스에 따로 띄우고, 도형·글자 자리는 그 아래에서 시작한다.
  const cin = split ? briefLayoutInputs(slide, W, baseAvail) : { band: null, avail: baseAvail, fitSlide: slide };
  const loose = briefFit(cin.fitSlide, W, H, cin.avail, tail, tailWidth);
  // 체크리스트가 보통 간격으로 안 들어가 글자가 줄어들 판이면 간격을 좁혀 다시 맞춘다(글자를 줄이기 전에 빈틈부터).
  //   단계·카드도 같은 차례(09-30 경계 불변식 — 자막 2줄 장면에서 통째 축소로 라벨이 42px 아래로 내려갔다).
  const cur = loose.contentScale < 1 && BRIEF_COMPACTABLE.has(slide.type) ? { ...briefFit(cin.fitSlide, W, H, cin.avail, tail, tailWidth, true), compact: true } : loose;
  // 📐 #6354 한 문장 장표 — 자막 윗선 위에 안 들어가면 «줄 간격(부제 1.3) → 두 열(16:9 · 긴 목록) → 글자 줄임(하한 0.5)» 차례로.
  //   들어가는 장면(배율 1)은 종전 계획 그대로 = 바이트 불변.
  if (loose.contentScale < 1 && slide.type === 'fullText') {
    const tight = { ...briefFit(cin.fitSlide, W, H, cin.avail, tail, tailWidth, true), compact: true };
    const cols = tight.contentScale < 1 && inferAspect(W, H) === '16:9' && briefFullTextSubCols(cin.fitSlide.content?.sub)
      ? { ...briefFit(cin.fitSlide, W, H, cin.avail, tail, tailWidth, true, true), compact: true, subCols: true } : null;
    return { k, core, cin, avail: cin.avail, cur: cols ?? tight };
  }
  return { k, core, cin, avail: cin.avail, cur };
}

export const MotionBriefStage: React.FC<MotionBriefStageProps> = ({ f, slide, prevSlide, sceneFrames, canvas, brand, reserveRightPx = 0, captionTopPx, captionBottomPx, captionWidthPx, prevCaption, titleColors, opening = false, wordCues, children }) => {
  const { width: W, height: H, fps } = useVideoConfig();
  const caption = typeof captionTopPx === 'number' && typeof captionBottomPx === 'number' ? { top: captionTopPx, bottom: captionBottomPx, width: captionWidthPx } : undefined;
  const split = titleColors !== undefined;
  const { k, core, cin, avail, cur } = briefStageFit(slide, W, H, reserveRightPx, caption, split);
  const to = cur.rect;
  const prevGeom = stageGeom(W, H, reserveRightPx, prevCaption ?? caption);
  const pin = prevSlide && split ? briefLayoutInputs(prevSlide, W, prevGeom.avail) : null;
  const from = prevSlide ? briefTargetRect(pin ? pin.fitSlide : prevSlide, W, H, pin ? pin.avail : prevGeom.avail, prevGeom.tail, prevGeom.tailWidth) : briefSeedRect(W, H);
  const t = Math.max(0, f) / fps;
  const rect = briefMorphRect(t, from, cur.shape);
  // 도형 색 변신 — 전환 길이(800ms · 부드러운 진입)로 섞는다(흰 ↔ 먹이 몇 프레임 만에 바뀌면 휘도 급변이 잡힌다).
  const fillFrom = prevSlide ? briefFillColor(briefFillOf(prevSlide.type), brand) : BRIEF_PAPER;
  const fillTo = briefFillColor(briefFillOf(slide.type), brand);
  const fill = fillFrom === fillTo ? fillTo : briefMixHex(fillFrom, fillTo, EASE_ENTER_SOFT(lin(t, 0, DURATION_MS.transition / 1000)));
  const endSec = typeof sceneFrames === 'number' && sceneFrames > 0 ? sceneFrames / fps - BRIEF_CONTENT_TAIL_SEC : undefined;
  // 창이 너무 짧은 장면(≤0.5초)은 빠지지 않게 둔다 — 켜지자마자 꺼지는 깜박임보다 하드컷이 낫다.
  const lead = briefContentLead(from, cur.shape, Math.round(24 * k));
  const alpha = win(t, lead, endSec !== undefined && endSec > 0.5 ? endSec : undefined, BRIEF_CONTENT_FADE_SEC);
  const poster = slide.type === 'posterStack';
  // 앞 장면이 장표가 아니면(영상·그림 씬) 캔버스가 전환 길이(800ms · 부드러운 진입)로 밝아진다 —
  //   어두운 영상 뒤 밝은 캔버스가 몇 프레임 만에 켜지면 1초 창 휘도 급변이 5번 잡혔다(09-30 운영 표본 실측).
  //   첫 장면은 켜진 채로 시작 — 페이드면 0프레임(포스터)에 아래 깔린 씬 그림이 드러났다(09-30 dev 실측 주제1).
  const canvasIn = prevSlide || opening ? 1 : EASE_ENTER_SOFT(lin(t, 0, DURATION_MS.transition / 1000));
  // 안 내용은 «목표 자리» 안에 놓인다(변신 중인 모양이 아니다) — 글자가 변신을 따라 줄바꿈되며 출렁이지 않게.
  const inset = poster
    // 포스터는 글자가 아래에서 올라오며 등장한다 — 자막 띠 위 24px 여유를 더 둔다(09-30 전 프레임 검사: 1프레임 6px 겹침).
    ? { ...core, bottom: Math.max(core.bottom, avail.bottom) + Math.round(24 * k) }
    : { top: to.y + BRIEF_PAD.y * k, left: to.x + BRIEF_PAD.x * k, right: W - (to.x + to.w) + BRIEF_PAD.x * k, bottom: H - (to.y + to.h) + cur.padBottom, ...(cin.band ? { hideTitle: true } : {}), ...(cur.compact ? { compact: true } : {}), ...(cur.subCols ? { subCols: true } : {}), ...(cur.cells ? { cells: cur.cells } : {}) };
  const band = cin.band;
  // ⏱🔤 큰 제목 = 그 낱말을 말하는 순간(없으면 종전 = 도형이 자리 잡은 직후).
  const titleAt = Math.round(titleCueLeadSec(lead, wordCues?.title, fps, endSec) * fps);
  const content = (slide.content ?? {}) as { keyword?: string; keywordEmphasis?: KeywordEmphasisId };
  const contentScale = cur.contentScale;
  // 🧊 큰 제목 고아 방지(마지막 두 어절 붙임 · text-wrap: balance 와 함께).
  const bandGlued = band ? briefGlueLastWords(band.text, band.fontPx, band.width) : '';
  // 📐 #6394 붙여도 마지막 줄이 한 어절이면 띠 폭·실제 글꼴로 재서 고르게 다시 나눈다(`title-balance.ts` · 고른 제목은 그대로).
  const [bandRef, bandText] = useBalancedTitle(bandGlued, { avail: 'self', keyword: content.keyword });
  return (
    <AbsoluteFill style={{ background: canvas, opacity: canvasIn }}>
      <div
        data-brief-shape={slide.type}
        style={{
          position: 'absolute', left: rect.x, top: rect.y, width: rect.w, height: rect.h, borderRadius: rect.r,
          background: fill, boxShadow: `0 ${Math.round(18 * k)}px ${Math.round(48 * k)}px rgba(17,24,39,0.10)`,
        }}
      />
      {band && titleColors ? (
        /* 도형 위 큰 제목 — 글자와 같은 창(늦게 들어와 다음 변신 전에 빠짐) · 어절 등장 · 강조 낱말(채팅 «강조» 편집이 여기서도 보인다). */
        <div ref={bandRef} data-brief-title="" style={{ position: 'absolute', left: band.left, top: band.top, width: band.width, textAlign: 'center', opacity: alpha,
          fontSize: band.fontPx, fontWeight: FW.extrabold, lineHeight: LINE_HEIGHT.tight, color: titleColors.textMain, ...KO_TEXT, textWrap: 'balance' }}>
          <KitWords kit fps={fps} f={f} at={titleAt} text={bandText}>
            <KeywordText text={bandText} keyword={content.keyword && !bandText.includes(content.keyword) ? content.keyword.replace(/ /g, '\u00A0') : content.keyword} emphasis={content.keywordEmphasis} colors={titleColors} f={f - titleAt - 12} />
          </KitWords>
        </div>
      ) : null}
      <SlideStageInsetContext.Provider value={inset}>
        <AbsoluteFill data-brief-content="" style={{ opacity: alpha, ...(contentScale < 1
          ? { transform: `scale(${contentScale.toFixed(4)})`, transformOrigin: `${Math.round(to.x + to.w / 2)}px ${Math.round(to.y + to.h / 2)}px` }
          : {}) }}>{children}</AbsoluteFill>
      </SlideStageInsetContext.Provider>
    </AbsoluteFill>
  );
};

/**
 * 🧊 나가는 무대 — 장표 장면 다음이 영상·그림 장면이면 캔버스와 도형이 전환 길이(800ms · 부드러운 진입의 역)로 빠진다.
 *   한 프레임 만에 꺼지면 밝은 캔버스 → 어두운 영상에서 평균 밝기가 177/255 떨어졌다(09-30 부모 실측 35.1초). 글자는 이미 빠진 뒤다(0.1초 전).
 */
export const MotionBriefExit: React.FC<{ f: number; prevSlide: BriefSlide; canvas: string; brand: string; reserveRightPx?: number; prevCaption?: BriefCaption; split?: boolean }> = ({ f, prevSlide, canvas, brand, reserveRightPx = 0, prevCaption, split = false }) => {
  const { width: W, height: H, fps } = useVideoConfig();
  const t = Math.max(0, f) / fps;
  const out = EASE_ENTER_SOFT(lin(t, 0, DURATION_MS.transition / 1000));
  if (out >= 1) return null;
  const { k, avail, tail, tailWidth } = stageGeom(W, H, reserveRightPx, prevCaption);
  const pin = split ? briefLayoutInputs(prevSlide, W, avail) : null;
  const rect = briefTargetRect(pin ? pin.fitSlide : prevSlide, W, H, pin ? pin.avail : avail, tail, tailWidth);
  return (
    <AbsoluteFill style={{ background: canvas, opacity: 1 - out }}>
      <div style={{ position: 'absolute', left: rect.x, top: rect.y, width: rect.w, height: rect.h, borderRadius: rect.r,
        background: briefFillColor(briefFillOf(prevSlide.type), brand), boxShadow: `0 ${Math.round(18 * k)}px ${Math.round(48 * k)}px rgba(17,24,39,0.10)` }} />
    </AbsoluteFill>
  );
};
